import { RefObject, useCallback, useEffect, useState } from "react";

/**
 * Drives the Fullscreen API for a single element and keeps React in step with
 * it — the user can also leave fullscreen with Escape or the browser chrome,
 * which never routes through our toggle.
 */
const useFullscreen = (
  targetRef: RefObject<HTMLElement>,
  /** Runs after entering or leaving, e.g. to re-measure the map. */
  onChange?: () => void,
) => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isSupported, setIsSupported] = useState<boolean>(true);

  useEffect(() => {
    setIsSupported(Boolean(document.fullscreenEnabled));
  }, []);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(document.fullscreenElement === targetRef.current);
      onChange?.();
    };
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, [targetRef, onChange]);

  const toggle = useCallback(async () => {
    const element = targetRef.current;
    if (!element) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await element.requestFullscreen();
      }
    } catch {
      // A rejected request (permissions, unsupported) simply leaves the page as
      // it is; the state stays in sync via the fullscreenchange listener.
    }
  }, [targetRef]);

  return { isFullscreen, isSupported, toggle };
};

export default useFullscreen;
